// @hssna/ui-kit/auth/MfaVerifyPage — MFA 验证页面
// 规格: SPEC §6 / §7 (安全合规) / S1 认证流程 / ADR-0012 (Tailwind) / OLD-01 (适老化)
//
// S1 MFA 流程:
//   1. 挂载时调用 authClient.mfaChallenge(mfaType, sessionId) 获取 challenge_id
//   2. 用户输入 6 位验证码
//   3. 提交调用 authClient.mfaVerify(challenge_id, code)
//   4. 验证通过 → onSuccess(mfaVerifyResponse)
//
// 6 位验证码输入:
//   - 6 个独立输入框, 自动聚焦下一格
//   - 仅允许数字 (粘贴/键盘)
//   - 适老化: 大字号 + 大输入区

import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  ClipboardEvent,
  FormEvent,
  KeyboardEvent,
} from 'react';
import type { AuthClient, MfaVerifyResponse } from '@hssna/ts-shared';
import { Button } from '../common/Button.js';
import { LoadingSpinner } from '../common/LoadingSpinner.js';
import type { AuthTokens, MfaType } from './LoginPage.js';

export interface MfaVerifyPageProps {
  /** S1 认证客户端 */
  readonly authClient: AuthClient;
  /** 登录阶段返回的 session_id */
  readonly sessionId: string;
  /** MFA 类型 (sms 短信 / totp 时间一次性密码) */
  readonly mfaType: MfaType;
  /** 验证成功回调 */
  readonly onSuccess: (tokens: AuthTokens) => void;
  /** 错误回调 */
  readonly onError?: (error: unknown) => void;
}

const CODE_LENGTH = 6;

export function MfaVerifyPage({
  authClient,
  sessionId,
  mfaType,
  onSuccess,
  onError,
}: MfaVerifyPageProps) {
  const [challengeId, setChallengeId] = useState<string | null>(null);
  const [challengeLoading, setChallengeLoading] = useState(true);
  const [code, setCode] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // 1. 挂载时发起 MFA 挑战
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const challenge = await authClient.mfaChallenge(mfaType, sessionId);
        if (cancelled) return;
        setChallengeId(challenge.challenge_id);
        setChallengeLoading(false);
        // 自动聚焦第一个输入框
        inputRefs.current[0]?.focus();
      } catch (error) {
        if (cancelled) return;
        setChallengeLoading(false);
        setErrorMsg('获取验证码失败,请重试');
        if (onError) {
          onError(error);
        } else {
          // eslint-disable-next-line no-console
          console.error('[MfaVerifyPage] mfaChallenge failed:', error);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authClient, mfaType, sessionId, onError]);

  // 2. 处理单个输入框变更
  const handleChange = useCallback(
    (index: number, value: string) => {
      // 仅保留数字
      const digit = value.replace(/\D/g, '').slice(-1);
      const next = [...code];
      next[index] = digit;
      setCode(next);
      setErrorMsg(null);

      // 自动聚焦下一格
      if (digit && index < CODE_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    },
    [code],
  );

  // 3. 处理退格 (空格时回退到上一格)
  const handleKeyDown = useCallback(
    (index: number, e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !code[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    },
    [code],
  );

  // 4. 处理粘贴 (拆分为 6 位) — 挂载在容器 div 上, 捕获子 input 的粘贴事件
  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLDivElement>) => {
      e.preventDefault();
      const text = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LENGTH);
      if (!text) return;
      const next = Array(CODE_LENGTH).fill('');
      for (let i = 0; i < text.length; i++) {
        next[i] = text[i] ?? '';
      }
      setCode(next);
      const focusIndex = Math.min(text.length, CODE_LENGTH - 1);
      inputRefs.current[focusIndex]?.focus();
    },
    [],
  );

  // 5. 提交验证
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const fullCode = code.join('');
    if (fullCode.length !== CODE_LENGTH) {
      setErrorMsg('请输入完整的 6 位验证码');
      return;
    }
    if (!challengeId) {
      setErrorMsg('验证挑战未就绪,请稍候');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);
    try {
      const response: MfaVerifyResponse = await authClient.mfaVerify(challengeId, fullCode);
      if (response.mfa_completed) {
        onSuccess(response);
      } else {
        setErrorMsg('验证未通过,请检查验证码');
      }
    } catch (error) {
      setErrorMsg('验证失败,请重试');
      if (onError) {
        onError(error);
      } else {
        // eslint-disable-next-line no-console
        console.error('[MfaVerifyPage] mfaVerify failed:', error);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (challengeLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 theme-elderly:bg-white">
        <div className="flex flex-col items-center gap-3 text-gray-600">
          <LoadingSpinner size="lg" />
          <p className="theme-elderly:text-elderly">正在获取验证码...</p>
        </div>
      </div>
    );
  }

  const mfaTypeLabel = mfaType === 'sms' ? '短信验证码' : '动态口令 (TOTP)';

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 px-4 py-8 theme-elderly:bg-white">
      <div className="w-full max-w-md">
        <div className="rounded-lg bg-white p-8 shadow-md theme-elderly:border-2 theme-elderly:border-elderly-border">
          <h1 className="mb-2 text-center text-2xl font-bold text-gray-900 theme-elderly:text-elderly-lg">
            多因素验证
          </h1>
          <p className="mb-6 text-center text-sm text-gray-600 theme-elderly:text-elderly">
            请输入 {mfaTypeLabel} 6 位验证码
          </p>

          <form onSubmit={handleSubmit} className="space-y-6" noValidate>
            <div
              className="flex justify-center gap-2 theme-elderly:gap-3"
              onPaste={handlePaste}
              role="group"
              aria-label="6 位验证码输入"
            >
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(el) => {
                    inputRefs.current[index] = el;
                  }}
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  maxLength={1}
                  value={digit}
                  onChange={(e) => handleChange(index, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(index, e)}
                  disabled={submitting}
                  aria-label={`第 ${index + 1} 位`}
                  className="h-14 w-12 rounded-md border-2 border-gray-300 text-center text-2xl font-bold text-gray-900 focus:border-brand-primary focus:outline-none focus:ring-2 focus:ring-brand-primary/30 disabled:bg-gray-100 theme-elderly:h-16 theme-elderly:w-16 theme-elderly:text-elderly-lg theme-elderly:border-elderly-border"
                />
              ))}
            </div>

            {errorMsg && (
              <p
                role="alert"
                className="text-center text-sm text-alert-red theme-elderly:text-base"
              >
                {errorMsg}
              </p>
            )}

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={submitting}
              className="w-full theme-elderly:text-elderly"
            >
              {submitting ? '验证中...' : '验证'}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
